import React from 'react';
import { Controller } from 'react-hook-form';
import type { ControllerRenderProps, UseFormMethods } from 'react-hook-form';
import get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import Alert from 'components/common/feedback/alert';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Checkbox from 'components/common/input-components/checkbox';
import { DesktopLabelText } from 'components/common/form-components';
import { setLink } from 'components/common/link';
import { links } from 'constants/external-links';
import styled from 'types/theme.types';
import type { CredentialType } from 'types/api.types';
import type { OidcTokenFormat } from '@env0/common-oidc';
import OidcClaimField from 'components/organizations/credentials/oidc-claim-field';
import { getOidcAudience } from 'components/organizations/credentials/oidc-audiences';

type OidcV2FieldsProps = {
  form: Pick<UseFormMethods<any>, 'control' | 'getValues'>;
  fieldName: string;
  credentialType: CredentialType;
};

const OidcV2Fields: React.FunctionComponent<OidcV2FieldsProps> = ({ form, fieldName, credentialType }) => {
  return (
    <Controller
      name={fieldName}
      control={form.control}
      defaultValue={get(form.getValues() as Record<string, unknown>, fieldName)}
      render={({ value, onChange }: ControllerRenderProps) => {
        const tokenFormat = value as OidcTokenFormat | undefined;
        const onToggle = (e: CheckboxChangeEvent): void => onChange(e.target.checked ? 'v2' : undefined);

        return (
          <>
            <CheckboxRow>
              <Checkbox checked={tokenFormat === 'v2'} onChange={onToggle} data-e2e="oidc-use-v2-token">
                <DesktopLabelText
                  id="settings.credentials.oidc.use-v2-token"
                  info={{
                    id: 'settings.credentials.oidc.use-v2-token.info',
                    values: setLink(links.docs.DEPLOYMENT_CREDENTIALS.OIDC_V2_TOKEN_DOCS)
                  }}
                />
              </Checkbox>
            </CheckboxRow>
            <OidcClaimField
              labelId="settings.credentials.oidc.audience"
              value={getOidcAudience(credentialType, tokenFormat)}
              dataE2e="oidc-audience"
            />
            {tokenFormat === 'v2' && (
              <AgentVersionAlert
                showIcon
                type="info"
                data-e2e="oidc-v2-agent-version-note"
                message={
                  <FormattedMessage
                    id="settings.credentials.oidc.agent-version-note"
                    values={setLink(links.docs.DEPLOYMENT_CREDENTIALS.OIDC_V2_TOKEN_DOCS)}
                  />
                }
              />
            )}
          </>
        );
      }}
    />
  );
};

const CheckboxRow = styled.div`
  margin: 4px 0 19px;
`;

const AgentVersionAlert = styled(Alert)`
  margin-top: 8px;
`;

export default OidcV2Fields;
